import { ReactElement, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import classNames from "classnames";
import LayoutContext from "../../../contexts/LayoutContext";
import FavoritosContext from "../../../contexts/FavoritosContext";
import MenuBarItemModel from "../../../models/layout/MenuBarItemModel";
import { esFavoritable, esRutaActiva } from "../../../utils/menuGroups";

interface MenuBarItemProps {
    menuBarItemModel: MenuBarItemModel;
    /** Abierta al montar aunque no contenga la pantalla actual. */
    defaultOpen?: boolean;
}

/** Botón de estrella de una pantalla; compartido por el panel y la lista de favoritos. */
export const FavoritoStar = ({ href, name }: { href: string; name: string }): ReactElement | null => {
    const { esFavorito, toggleFavorito } = useContext(FavoritosContext);
    if (!esFavoritable(href)) return null;
    const marcado = esFavorito(href);
    return (
        <button
            type="button"
            className={classNames('menu-star', { marcado })}
            onClick={(e) => { e.stopPropagation(); toggleFavorito(href); }}
            aria-pressed={marcado}
            aria-label={marcado ? `Quitar ${name} de favoritos` : `Agregar ${name} a favoritos`}
            title={marcado ? 'Quitar de favoritos' : 'Agregar a favoritos'}
        >
            <i className={classNames('fa', marcado ? 'fa-star' : 'fa-star-o')} aria-hidden="true" />
        </button>
    );
};

/** Si alguna pantalla de la lista (o de sus subgrupos) es la página actual. */
const contieneRutaActiva = (items: MenuBarItemModel[], pathname: string): boolean =>
    items.some((s) => (s.subMenu ? contieneRutaActiva(s.subMenu, pathname) : esRutaActiva(s.href, pathname)));

/**
 * Subgrupo dentro de una sección (p. ej. SGI → "Gestión de Calidad"), con sus
 * pantallas sangradas. Se abre solo si contiene la página actual.
 */
const SubgrupoMenu = ({ grupo, navegar }: { grupo: MenuBarItemModel; navegar: (href: string) => void }): ReactElement => {
    const { pathname } = useLocation();
    const pantallas = grupo.subMenu || [];
    const activo = contieneRutaActiva(pantallas, pathname);
    const [abierto, setAbierto] = useState(activo);

    useEffect(() => {
        if (activo) setAbierto(true);
    }, [activo]);

    return (
        <div className={classNames('menu-subgrupo', { abierto })}>
            <button
                type="button"
                className={classNames('menu-subgrupo-btn', { active: activo })}
                onClick={() => setAbierto((prev) => !prev)}
                aria-expanded={abierto}
            >
                {grupo.icon && <i className={`fa ${grupo.icon} menu-subgrupo-icon`} aria-hidden="true" />}
                <span className="menu-text">{grupo.name}</span>
                <i className={classNames('fa fa-chevron-down menu-chevron', { open: abierto })} aria-hidden="true" />
            </button>
            {abierto && pantallas.map((sub) => (
                <div
                    key={`${sub.href}-${sub.name}`}
                    className={classNames('menu-pantalla', 'menu-pantalla-anidada', { active: esRutaActiva(sub.href, pathname) })}
                >
                    <button type="button" className="menu-pantalla-link" onClick={() => navegar(sub.href)}>
                        {sub.name}
                    </button>
                    <FavoritoStar href={sub.href} name={sub.name} />
                </div>
            ))}
        </div>
    );
};

/** Sección del panel (p. ej. "Calidad") con sus pantallas desplegables. */
const MenuBarItem = ({ menuBarItemModel, defaultOpen = false }: MenuBarItemProps): ReactElement => {
    const { name, icon, subMenu = [] } = menuBarItemModel;
    const { toggleMenuBar } = useContext(LayoutContext);
    const history = useHistory();
    const { pathname } = useLocation();

    const hasActiveChild = contieneRutaActiva(subMenu, pathname);
    const [isOpen, setIsOpen] = useState(defaultOpen || hasActiveChild);

    // Al navegar a una pantalla de esta sección (p. ej. desde favoritos), se abre.
    useEffect(() => {
        if (hasActiveChild) setIsOpen(true);
    }, [hasActiveChild]);

    const navegar = (href: string): void => {
        toggleMenuBar(false);
        history.push(href);
    };

    return (
        <div className={classNames('menu-seccion', { abierta: isOpen })}>
            <button
                type="button"
                className={classNames('menuButton', { active: hasActiveChild })}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
            >
                {icon && (
                    <span className="menu-icon">
                        <i className={`fa ${icon}`} aria-hidden="true" />
                    </span>
                )}
                <span className="menu-text">{name}</span>
                <i className={classNames('fa fa-chevron-down menu-chevron', { open: isOpen })} aria-hidden="true" />
            </button>

            {isOpen && (
                <div className="submenu-container">
                    {subMenu.map((sub) => (sub.subMenu ? (
                        <SubgrupoMenu key={`${sub.href}-${sub.name}`} grupo={sub} navegar={navegar} />
                    ) : (
                        <div
                            key={`${sub.href}-${sub.name}`}
                            className={classNames('menu-pantalla', { active: esRutaActiva(sub.href, pathname) })}
                        >
                            <button type="button" className="menu-pantalla-link" onClick={() => navegar(sub.href)}>
                                {sub.name}
                            </button>
                            <FavoritoStar href={sub.href} name={sub.name} />
                        </div>
                    )))}
                </div>
            )}
        </div>
    );
};

export default MenuBarItem;
